import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, COACH_TOKEN_KEY, LEADER_TOKEN_KEY } from "../lib/api";

/* Two-user-type auth context. Coach + leader can be signed in simultaneously
 * (different localStorage keys, different tokens). Each call to /me uses the
 * matching token via the axios interceptor. */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [coach, setCoach] = useState(null); // null = unknown / loading, false = signed-out, object = signed-in
  const [leader, setLeader] = useState(null);

  // Initial check: ping /me endpoints if tokens exist; else mark signed-out
  const refresh = useCallback(async () => {
    const coachToken = localStorage.getItem(COACH_TOKEN_KEY);
    if (coachToken) {
      try {
        const { data } = await api.get("/auth/coach/me");
        setCoach(data);
      } catch {
        localStorage.removeItem(COACH_TOKEN_KEY);
        setCoach(false);
      }
    } else {
      setCoach(false);
    }
    const leaderToken = localStorage.getItem(LEADER_TOKEN_KEY);
    if (leaderToken) {
      try {
        const { data } = await api.get("/auth/leader/me");
        setLeader(data);
      } catch {
        localStorage.removeItem(LEADER_TOKEN_KEY);
        setLeader(false);
      }
    } else {
      setLeader(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signInCoach = async (email, password) => {
    const { data } = await api.post("/auth/coach/login", { email, password });
    localStorage.setItem(COACH_TOKEN_KEY, data.token);
    setCoach(data.user);
    return data.user;
  };
  const registerCoach = async ({ email, password, name }) => {
    const { data } = await api.post("/auth/coach/register", { email, password, name });
    localStorage.setItem(COACH_TOKEN_KEY, data.token);
    setCoach(data.user);
    return data.user;
  };
  const signOutCoach = () => {
    localStorage.removeItem(COACH_TOKEN_KEY);
    setCoach(false);
  };

  const signInLeaderPassword = async (email, password) => {
    const { data } = await api.post("/auth/leader/login", { email, password });
    localStorage.setItem(LEADER_TOKEN_KEY, data.token);
    setLeader(data.user);
    return data.user;
  };
  const registerLeader = async ({ email, password, name }) => {
    const { data } = await api.post("/auth/leader/register", { email, password, name });
    localStorage.setItem(LEADER_TOKEN_KEY, data.token);
    setLeader(data.user);
    return data.user;
  };
  const requestMagicLink = async (email) => {
    await api.post("/auth/leader/magic-request", { email });
  };
  const consumeMagicToken = async (token) => {
    const { data } = await api.post("/auth/leader/magic-consume", { token });
    localStorage.setItem(LEADER_TOKEN_KEY, data.token);
    setLeader(data.user);
    return data.user;
  };
  const signOutLeader = () => {
    localStorage.removeItem(LEADER_TOKEN_KEY);
    setLeader(false);
  };

  const value = {
    coach,
    leader,
    refresh,
    signInCoach,
    registerCoach,
    signOutCoach,
    signInLeaderPassword,
    registerLeader,
    requestMagicLink,
    consumeMagicToken,
    signOutLeader,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
